import React, { useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Group,
  Loader,
  Text,
  Pagination,
  Stack,
} from '@mantine/core';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  getPaginationRowModel,
} from '@tanstack/react-table';

import EmptyMessage from './EmptyMessage';

export default function PaginatedList({
  tableData = null,
  displayFormatter = () => <div>data</div>,
  responseMapping = null,
  requestInfo = { url: '', data: {} },
}) {
  const hasSeedData = Array.isArray(tableData);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });

  const pageIndex =
    pagination.pageIndex === -1 ? 0 : pagination.pageIndex;

  const listQuery = useQuery({
    queryKey: [
      'paginated-list',
      requestInfo?.url,
      JSON.stringify(requestInfo?.data || {}),
      pageIndex,
      pagination.pageSize,
    ],
    queryFn: async ({ signal }) => {
      const req = {
        ...(requestInfo.data || {}),
        page_size: pagination.pageSize,
        page: pageIndex,
      };
      const { data } = await axios.post(requestInfo.url, req, { signal });
      const payload = data.response?.[0] || {};
      const rows = payload.data || [];
      return {
        items: responseMapping ? responseMapping(rows) : rows,
        pageCount: payload.page_count || 0,
        total: payload.total !== undefined ? payload.total : rows.length,
      };
    },
    enabled: !hasSeedData && Boolean(requestInfo?.url),
    placeholderData: keepPreviousData,
  });

  const items = useMemo(() => {
    if (hasSeedData) return tableData;
    return listQuery.data?.items || [];
  }, [hasSeedData, tableData, listQuery.data?.items]);

  const pageCount = hasSeedData
    ? Math.ceil(tableData.length / pagination.pageSize) || 0
    : listQuery.data?.pageCount || 0;

  const total = hasSeedData
    ? tableData.length
    : listQuery.data?.total || 0;

  const loading = !hasSeedData && listQuery.isFetching;
  const error =
    !hasSeedData && listQuery.isError
      ? typeof listQuery.error === 'string'
        ? listQuery.error
        : 'Unable to load list'
      : null;
  const init = hasSeedData || listQuery.isFetched;

  const dynamicConfig = {
    manualPagination: !hasSeedData,
  };

  if (!hasSeedData) {
    dynamicConfig.pageCount = pageCount > 0 ? pageCount : -1;
  }

  const table = useReactTable({
    ...dynamicConfig,
    data: items,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
  });

  return (
    <div style={{ marginTop: '0.5em' }}>
      {!loading && total === 0 && !error && <EmptyMessage />}
      {error && !loading && init && (
        <Alert variant="light" color="yellow" title="Oops!">
          {error}
        </Alert>
      )}
      <Stack gap="sm">
        {table.getRowModel().rows.map((item) => displayFormatter(item))}
      </Stack>
      <Group mt="lg" align="center">
        <Pagination
          total={pageCount}
          value={pageIndex + 1}
          onChange={(e) => table.setPageIndex(e - 1)}
          size="lg"
          disabled={loading}
        />
        <Text size="sm">
          {total} total item{total === 1 ? '' : 's'}
        </Text>
        {loading && <Loader size="xs" type="dots" />}
      </Group>
    </div>
  );
}
